import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { useAuth } from "../../routers/AuthProvider";
import { useForm } from "../../hooks/useForm";
import backArrowIcon from "../../assets/img/back-arrow.png";
import "../../assets/css/common.css";
import { API_URL } from "../../utils/constant";
import { Toast } from "../../components/ToastComponent";

const STOCK_IMPORT_ENDPOINT = "/stock-import";

const emptyRow = () => ({
  product_id: "",
  product_name: "",
  product_code: "",
  barcode: "",
  product_category_name: "",
  unit_id: "",
  qty: "",
  units: [],
});

const CreateStockImport = () => {
  const backPath = "/warehouse/stock-import/list/";
  const navigate = useNavigate();
  const { setLoading } = useAuth();
  const { handleInputChange, formData, submitForm, setError, error } =
    useForm();

  const [warehouses, setWarehouses] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [products, setProducts] = useState(null);
  const [rows, setRows] = useState([emptyRow()]);

  useEffect(() => {
    if (error) {
      Toast.fire({ title: error, icon: "error" });
    }
  }, [error]);

  useEffect(() => {
    axios
      .get(`${API_URL}/warehouse?status=ACTIVE`, { withCredentials: true })
      .then((res) => setWarehouses(res.data?.data || []))
      .catch(() =>
        Toast.fire({ title: "Failed to load warehouses", icon: "error" })
      );
  }, []);

  useEffect(() => {
    axios
      .get(`${API_URL}/productCategory?status=ACTIVE`, {
        withCredentials: true,
      })
      .then((res) => {
        const list = res.data?.data || [];
        const map = {};
        list.forEach((category) => {
          map[category.product_category_id] = category.product_category_name;
        });
        setCategoryMap(map);
      })
      .catch(() =>
        Toast.fire({ title: "Failed to load categories", icon: "error" })
      );
  }, []);

  useEffect(() => {
    axios
      .get(`${API_URL}/product/no-price`, { withCredentials: true })
      .then((res) => {
        setProducts(res.data?.data || []);
      })
      .catch(() =>
        Toast.fire({ title: "Failed to load products", icon: "error" })
      );
  }, []);

  const mapProductsToOptions = (list, term) =>
    list
      .filter((product) => {
        const productName = product.product_name?.toLowerCase() || "";
        const barcode = product.barcode?.toLowerCase() || "";
        const prefixCode = product.prefix_code?.toLowerCase() || "";

        return (
          productName.includes(term) ||
          barcode.includes(term) ||
          prefixCode.includes(term)
        );
      })
      .map((product) => ({
        value: product.product_id,
        label: `${product.product_name} (${product.prefix_code || ""})`,
        _raw: product,
      }));

  const loadProductOptions = (inputValue) =>
    new Promise((resolve, reject) => {
      const term = (inputValue || "").trim().toLowerCase();

      if (products) {
        resolve(mapProductsToOptions(products, term));
        return;
      }

      resolve([]);
    });

  const fetchUnitsForProduct = (productId) =>
    axios
      .get(`${API_URL}/adjustment/product-units?product_id=${productId}`, {
        withCredentials: true,
      })
      .then((res) => res.data?.data || [])
      .catch(() => []);

  const handleProductSelect = async (rowIdx, option) => {
    const raw = option?._raw;
    const units = raw ? await fetchUnitsForProduct(raw.product_id) : [];
    const categoryNameFromRaw = raw?.product_category_name;
    const categoryNameFromMap = raw?.product_category_id
      ? categoryMap[raw.product_category_id]
      : "";

    setRows((prev) => {
      const next = [...prev];
      next[rowIdx] = {
        ...next[rowIdx],
        product_id: raw?.product_id || "",
        product_name: raw?.product_name || "",
        product_code: raw?.product_code || "",
        barcode: raw?.barcode || "",
        product_category_name: categoryNameFromRaw || categoryNameFromMap || "",
        unit_id: "",
        qty: "",
        units: units.map((unit) => ({
          unit_id: unit.unit_id,
          unit_name: unit.unit_name,
        })),
      };
      return next;
    });
  };

  const handleRowChange = (rowIdx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [field]: value };
      return next;
    });
  };

  const handleAddRow = () => setRows((prev) => [...prev, emptyRow()]);
  const handleRemoveRow = (rowIdx) =>
    setRows((prev) => prev.filter((_, index) => index !== rowIdx));

  const onSubmit = (e) => {
    e.preventDefault();

    if (!formData?.warehouse_id) return setError("Please select a warehouse.");
    if (!formData?.import_date)
      return setError("Please choose an import date.");
    if (!rows.length) return setError("Please add at least one product.");

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.product_id) {
        return setError(`Row ${i + 1}: product is required.`);
      }
      if (!row.unit_id) return setError(`Row ${i + 1}: unit is required.`);
      if (!row.qty || Number(row.qty) <= 0) {
        return setError(`Row ${i + 1}: quantity must be > 0.`);
      }
    }

    const payload = {
      warehouse_id: formData.warehouse_id,
      import_date: formData.import_date,
      remark: formData.remark || "",
      products: rows.map((row) => ({
        product_id: row.product_id,
        unit_id: row.unit_id,
        qty: Number(row.qty),
      })),
    };

    setLoading(true);
    submitForm(
      "POST",
      STOCK_IMPORT_ENDPOINT,
      payload,
      () => {
        setLoading(false);
        Toast.fire({ title: "Successfully Created", icon: "success" });
        navigate(backPath);
      },
      "Successfully Created"
    );
  };

  return (
    <div className="sub-page-layout-1">
      <Link className="back-to-page" to={backPath}>
        <img src={backArrowIcon} alt="back" />
        Back
      </Link>

      <div className="container" style={{ height: "auto" }}>
        <h1 className="sub-page-title">Create New Stock Import</h1>

        <div className="form-container">
          <form
            onSubmit={onSubmit}
            className="form flex flex-col justify-between w-full"
          >
            <h2 className="sub-title mb-8">Stock Import Information</h2>

            <div className="grid grid-cols-2 gap-9">
              <div className="grow">
                <label className="x-label input-required">Warehouse Name</label>
                <select
                  name="warehouse_id"
                  className="x-input dropdown"
                  defaultValue=""
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.warehouse_id}
                      disabled={warehouse.status !== "ACTIVE"}
                      value={warehouse.warehouse_id}
                    >
                      {warehouse.warehouse_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grow">
                <label className="x-label input-required">
                  Stock Import Date
                </label>
                <input
                  type="date"
                  required
                  name="import_date"
                  onChange={handleInputChange}
                  className="x-input"
                />
              </div>

              <div className="grow">
                <label className="x-label">Remark</label>
                <input
                  type="text"
                  name="remark"
                  onChange={handleInputChange}
                  className="x-input"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="stock-import-head">
              <h3 className="title text-primary-dark">
                Product Detail <span className="ml-1 text-red-500">*</span>
              </h3>
              <button
                type="button"
                onClick={handleAddRow}
                className="add-more text-primary-dark"
              >
                Add More Product +
              </button>
            </div>

            <div className="overflow-x-auto hide-scrollbar mt-2">
              <table className="custom-table stock-import-table">
                <thead>
                  <tr>
                    <th className="col-product">Product</th>
                    <th className="col-code">Product Code</th>
                    <th className="col-barcode">Barcode</th>
                    <th className="col-category">Product Category</th>
                    <th className="col-unit">Unit</th>
                    <th className="col-qty">Qty</th>
                    <th className="col-action">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.length ? (
                    rows.map((row, index) => (
                      <tr key={index}>
                        <td className="col-product">
                          <AsyncSelect
                            defaultOptions={mapProductsToOptions(
                              products || [],
                              ""
                            )}
                            cacheOptions
                            loadOptions={loadProductOptions}
                            isSearchable
                            onChange={(option) =>
                              handleProductSelect(index, option)
                            }
                            placeholder="Search product..."
                            noOptionsMessage={() =>
                              products ? "No products found" : "Loading products..."
                            }
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: 48,
                                borderColor: "#1b1b1b66",
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </td>

                        <td className="col-code">
                          <input
                            className="x-input"
                            value={row.product_code}
                            placeholder="Product Code"
                            readOnly
                            disabled
                          />
                        </td>

                        <td className="col-barcode">
                          <input
                            className="x-input"
                            value={row.barcode}
                            placeholder="Barcode"
                            readOnly
                            disabled
                          />
                        </td>

                        <td className="col-category">
                          <input
                            className="x-input"
                            value={row.product_category_name || ""}
                            readOnly
                            disabled
                          />
                        </td>

                        <td className="col-unit">
                          <select
                            className="x-input dropdown"
                            value={row.unit_id}
                            onChange={(e) =>
                              handleRowChange(index, "unit_id", e.target.value)
                            }
                            disabled={!row.units.length}
                          >
                            <option value="" disabled>
                              {row.units.length ? "Select" : "No units"}
                            </option>
                            {row.units.map((unit) => (
                              <option key={unit.unit_id} value={unit.unit_id}>
                                {unit.unit_name}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="col-qty">
                          <input
                            type="number"
                            min="1"
                            className="x-input"
                            value={row.qty}
                            onChange={(e) =>
                              handleRowChange(index, "qty", e.target.value)
                            }
                          />
                        </td>

                        <td className="col-action">
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => handleRemoveRow(index)}
                            aria-label="Remove"
                            title="Remove"
                          >
                            x
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center text-red-600 italic py-6"
                      >
                        At least one product is required.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-8 flex justify-end gap-5">
              <Link to={backPath} className="white-btn">
                Cancel
              </Link>
              <button type="submit" className="gold-btn">
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateStockImport;
